import Button from "@/components/atoms/Button";
import { DatePicker } from "@/components/atoms/DatePicker";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import Input from "@/components/atoms/Input";
import TicketSchema from "@/schemas/ticket";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import Ticket from "@/types/Ticket";
import Select from "@/components/molecules/Select";
import { Account } from "@/types/Account";
import Link from "@/components/atoms/Link";
import ErrorType from "@/types/ErrorType";
import { capitalize } from "@/lib/utils";
import type { NestedList, NestedItem } from "@/types/NestedItem";
import FileUpload from "@/components/atoms/FileUpload";
import axios from 'axios';
import { FieldValues } from 'react-hook-form';
import { useState } from 'react';
import CKEditorWithFileUpload from "@/components/atoms/CKEditor";
import Priority from "@/types/Priority";
import Spinner from '@/components/atoms/Spinner';
import InternalTicketTypeSection from "./InternalTicketTypeSection";

export type TicketProps = {
  accounts: Account[];
  internalOwners?: { id: number; name: string }[];
  sows?: any[];
  priorities: Priority[];
  requesters?: { id: number; name: string }[];
  ticket_types: NestedList[];
  internal_ticket_types?: any[];
  countries: NestedList[];
  functions: NestedList[];
  departments: NestedList[];
};

type Props = {
  mode?: "create" | "edit";
  ticket: any;
  edit: boolean;
  onSubmit: (data: Ticket) => void;
  data: TicketProps;
  loading: boolean;
  form: UseFormReturn<any>;
  errors?: ErrorType;
};
interface HandleAccountChangeParams {
  value: string;
  field: FieldValues;
  data: TicketProps;
  setData: React.Dispatch<React.SetStateAction<TicketProps>>;
}

interface HandleInternalTicketTypeChangeParams {
  value: string;
  field: FieldValues;
}

export const useTicketForm = (ticket: Ticket | null) =>

  useForm({
    resolver: zodResolver(TicketSchema),
    defaultValues: ticket
      ? {
        ...ticket,
        account_id: ticket.account.id.toString(),
        internal_owner_id: ticket.internal_owner?.id?.toString() || "",
        sow_id: ticket.sow?.id?.toString() || "",
        priority_id: ticket.priority?.id?.toString(),
        owner_id: ticket.requester?.id?.toString(),
        ticket_type_id: ticket.ticket_type?.id?.toString(),
        sub_ticket_type_id: ticket.sub_ticket_type?.id?.toString() || "",
        tickets_internal_ticket_types_attributes: ticket.internal_ticket_types?.map((obj) => ({
          id: obj.id.toString(),
          internal_ticket_type_id: obj.internal_ticket_type.id.toString(),
          internal_sub_ticket_type_id: obj.internal_sub_ticket_type.id.toString(),
          internal_sub_ticket_type_count: obj.internal_sub_ticket_type_count,
        })) || [],
        region_id: ticket.region?.id?.toString() || "",
        country_ids: ticket.countries?.map((obj) => obj.id.toString()) || [],
        function_id: ticket.function?.id?.toString(),
        sub_function_id: ticket.sub_function?.id?.toString() || "",
        department_id: ticket.department?.id?.toString(),
        sub_department_id: ticket.sub_department?.id?.toString() || "",
      }
      : {
        account_id: "",
        internal_owner_id: "",
        sow_id: "",
        subject: "",
        description: "",
        due_date: null,
        priority_id: "",
        owner_id: "",
        ticket_type_id: "",
        sub_ticket_type_id: "",
        tickets_internal_ticket_types_attributes: [],
        region_id: "",
        country_ids: [],
        function_id: "",
        sub_function_id: "",
        department_id: "",
        sub_department_id: "",
      },
  });


const TicketForm = ({
  form,
  mode = "edit",
  edit,
  onSubmit,
  ticket,
  data,
  loading: initialLoading,
  errors
}: Props) => {
  const [formData, setFormData] = useState(data);
  const [selectedAccountId, setSelectedAccountId] = useState<string>(ticket?.account?.id?.toString() || "");
  const [editorData, setEditorData] = useState(ticket?.description || "");
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unFilteredCountries, setUnFilteredCountries] = useState<any[]>([]);
  const [unFilteredFunctions, setUnFilteredFunctions] = useState<any[]>([]);
  const [unFilteredDepartments, setUnFilteredDepartments] = useState<any[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [subTicketTypes, setSubTicketTypes] = useState<NestedItem[]>([]);
  const [subFunctions, setSubFunctions] = useState<NestedItem[]>([]);
  const [subDepartments, setSubDepartments] = useState<NestedItem[]>([]);
  const [subCountries, setSubCountries] = useState<NestedItem[]>([]);

  const handleSubmit = (data: any) => {
    setLoading(true);
    const payload = {
      ticket: {
        ...data,
        tickets_internal_ticket_types_attributes: data.tickets_internal_ticket_types_attributes,
      },
      files: (form.getValues("files") || []).map((file: any) => ({
        filename: file.name,
        filesize: file.size,
        filetype: file.content_type,
        filepath: file.path,
        original_name: file.original_name,
        fileurl: file.url,
      })),
    };

    onSubmit(payload as any);
  };

  useEffect(() => {
    setLoading(initialLoading);
  }, [initialLoading]);
  
  useEffect(() => {
    if (!edit && ticket) {
        form.reset({
          ...ticket,
          internal_owner_id: ticket?.internal_owner?.id?.toString() || "",
          sow_id: ticket?.sow?.id?.toString() || "",
          priority_id: ticket?.priority?.id?.toString(),
          owner_id: ticket?.requester?.id?.toString(),
          ticket_type_id: ticket?.ticket_type?.id?.toString(),
          sub_ticket_type_id: ticket?.sub_ticket_type?.id?.toString() || "",
          tickets_internal_ticket_types_attributes: ticket?.internal_ticket_types?.map((obj: any) => ({
            id: obj.id.toString(),
            internal_ticket_type_id: obj.internal_ticket_type.id.toString(),
            internal_sub_ticket_type_id: obj.internal_sub_ticket_type.id.toString(),
            internal_sub_ticket_type_count: obj.internal_sub_ticket_type_count,
          })) || [],
          region_id: ticket?.region?.id?.toString() || "",
          country_ids: ticket.region_id != null
            ? (ticket.countries?.[0]?.children?.map((child: any) => child.id.toString()) || [])
            : (ticket.countries?.map((obj: any) => obj.id.toString()) || []),
          function_id: ticket?.function?.id?.toString(),
          sub_function_id: ticket?.sub_function?.id?.toString() || "",
          department_id: ticket?.department?.id?.toString(),
          sub_department_id: ticket?.sub_department?.id?.toString() || "",
          files: [],
        });
      }

      if (ticket) {
        if (ticket.region) {
          const region = formData.countries.find((item: any) => item.id === ticket.region.id);
          if (region) {
            setSubCountries(region.children);
          }
        }

        if (ticket.function) {
          const func = formData.functions.find((item: any) => item.id === ticket.function.id);
          if (func) {
            setSubFunctions(func.children);
          }
        }

        if (ticket.department) {
          const dept = formData.departments.find((item: any) => item.id === ticket.department.id);
          if (dept) {
            setSubDepartments(dept.children);
          }
        }

        if (ticket.ticket_type) {
          const ticketType = formData.ticket_types.find((item: any) => item.id === ticket.ticket_type.id);
          if (ticketType && ticketType.children) {
            setSubTicketTypes(ticketType.children);
          }
        }
      }
  }, [edit, ticket]);

  useEffect(() => {
    if (ticket) {
      form.reset({
        ...ticket,
        internal_owner_id: ticket?.internal_owner?.id?.toString() || "",
        sow_id: ticket?.sow?.id?.toString() || "",
        priority_id: ticket?.priority?.id?.toString(),
        owner_id: ticket?.requester?.id?.toString(),
        ticket_type_id: ticket?.ticket_type?.id?.toString(),
        sub_ticket_type_id: ticket?.sub_ticket_type?.id?.toString() || "",
        tickets_internal_ticket_types_attributes: ticket?.internal_ticket_types?.map((obj: any) => ({
          id: obj.id.toString(),
          internal_ticket_type_id: obj.internal_ticket_type.id.toString(),
          internal_sub_ticket_type_id: obj.internal_sub_ticket_type.id.toString(),
          internal_sub_ticket_type_count: obj.internal_sub_ticket_type_count,
        })) || [],
        region_id: ticket?.region?.id?.toString() || "",
        country_ids: ticket.region_id != null
          ? (ticket.countries?.[0]?.children?.map((child: any) => child.id.toString()) || [])
          : (ticket.countries?.map((obj: any) => obj.id.toString()) || []),
        function_id: ticket?.function?.id?.toString(),
        sub_function_id: ticket?.sub_function?.id?.toString() || "",
        department_id: ticket?.department?.id?.toString(),
        sub_department_id: ticket?.sub_department?.id?.toString() || "",
        files: [],
      });
      setEditorData(ticket.description || "");
    }
  }, [ticket]);

  useEffect(() => {
    if (errors) {
      setLoading(false);
      Object.entries(errors).forEach(([key, messages]) => {
        form.setError(key as keyof typeof form.getValues, {
          type: "manual",
          message: `${capitalize(key)} ${messages[0]}`,
        });
      });
    }
  }
    , [errors]);

  useEffect(() => {
    if (mode === "edit") {
      setTimeout(() => {
        axios
          .get(`/tickets/filter_sows_for_ticket`, {
            params: {
              account_id: ticket.account.id,
              internal_owner_id: ticket.internal_owner.id,
            }
          })
          .then(response => {
            setFormData({
              ...formData,
              sows: response.data,
            });
          })
          .catch(error => console.error('Error fetching filter_sows_for_ticket:', error));
      }, 50);
    }
  }, [data]);

  async function handleAccountChange({ value, field, data, setData }: HandleAccountChangeParams) {
    field.onChange(value);
    setLoading(true);
    const response = await axios.get(`/accounts/${value}/read_account_for_ticket`);
   
    setData({
      ...data,
      internalOwners: response.data.users.filter(
        (user: any) =>
          user.status === "Active" &&
          !user.roles.some((role: any) => role.name === "client")
      ),
      sows: [],
      priorities: response.data.priorities,
      requesters: response.data.users.filter(
        (user: any) =>
          user.status === "Active" &&
          user.roles.some((role: any) => role.name === "client")
      ),
      ticket_types: response.data.ticket_types,
      internal_ticket_types: response.data.internal_ticket_types,
      countries: response.data.countries,
      functions: response.data.functions,
      departments: response.data.departments,
    });
    setUnFilteredCountries(response.data.countries);
    setUnFilteredDepartments(response.data.departments);
    setUnFilteredFunctions(response.data.functions);
    setSelectedAccountId(value);
    setLoading(false);
  }

  async function handleInternalOwnerChange({ value, field, data, setData }: HandleAccountChangeParams) {
    field.onChange(value);
    
    const response = await axios.get(`/tickets/filter_sows_for_ticket`, {
      params: {
        account_id: selectedAccountId,
        internal_owner_id: value,
      }
    });
   
    setData({
      ...data,
      sows: response.data,
    });
  
  }

  async function handleSowChange({ value, field, data, setData }: HandleAccountChangeParams) {
    field.onChange(value);

    const selectedSow = (data.sows || []).find((sow: any) => sow.id.toString() === value);
  
    if (selectedSow) {
      if (selectedSow.region_id) {
        if (selectedSow.country_ids.length > 1) {
          setData({
            ...data,
            countries: unFilteredCountries
              .filter((country: any) => {
                const isRegionMatch = country.id.toString() === selectedSow.region_id.toString();
          
                const hasMatchingChildren = country.children.some((child: any) =>
                  selectedSow.country_ids.includes(child.id)
                );
          
                return isRegionMatch || hasMatchingChildren;
              })
              .map((country: any) => {
                const filteredChildren = country.children.filter((child: any) =>
                  selectedSow.country_ids.includes(child.id)
                );
          
                return {
                  ...country,
                  children: filteredChildren.length > 0 ? filteredChildren : country.children,
                };
              }),
          });
          form.setValue("region_id", selectedSow.region_id.toString());
          form.setValue("country_ids", "");
        } else if (selectedSow.country_ids.length === 1) {
          setData({
            ...data,
            countries: unFilteredCountries
              .filter((country: any) => {
                const isRegionMatch = country.id.toString() === selectedSow.region_id.toString();
          
                const hasMatchingChildren = country.children.some((child: any) =>
                  selectedSow.country_ids.includes(child.id)
                );
          
                return isRegionMatch || hasMatchingChildren;
              })
              .map((country: any) => {
                const filteredChildren = country.children.filter((child: any) =>
                  selectedSow.country_ids.includes(child.id)
                );
          
                return {
                  ...country,
                  children: filteredChildren.length > 0 ? filteredChildren : country.children,
                };
              }),
          });
          form.setValue("region_id", selectedSow.region_id.toString());
          form.setValue("country_ids", [selectedSow.country_ids[0].toString()]);
        } else {
          setData({
            ...data,
            countries: unFilteredCountries.filter((country:any) => country.id.toString() === selectedSow.region_id.toString()),
          });
          form.setValue("region_id", selectedSow.region_id.toString());
          form.setValue("country_ids", "");
          setSubCountries(data.countries.filter((country: any) => country.id === selectedSow.region_id)[0].children);
        }
      } else {
        if (selectedSow.country_ids.length === 1) {
          const countryId = selectedSow.country_ids[0];
  
          setData({
            ...data,
            countries: unFilteredCountries.filter((country:any) => country.id.toString() === countryId.toString()),
          });
          form.setValue("country_ids", [countryId.toString()]);
        } else if (selectedSow.country_ids.length > 1) {

          setData({
            ...data,
            countries: unFilteredCountries.filter((country:any) =>
              selectedSow.country_ids.includes(country.id)
            ),
          });

          form.setValue("country_ids", "");
        } else {
          setData({
            ...data,
            countries: unFilteredCountries,
          });
          form.setValue("country_ids", "");
        }
      }

      if (selectedSow.function_ids) {
        if (selectedSow.function_ids.length > 1) {
          setData({
            ...data,
            functions: unFilteredFunctions.filter((func: any) =>
              selectedSow.function_ids.includes(func.id)
            ),
          });

          form.setValue("function_id", "");
        } else if (selectedSow.function_ids.length === 1) {
          const functionId = selectedSow.function_ids[0];
          setData({
            ...data,
            functions: unFilteredFunctions.filter((func: any) => func.id.toString() === functionId.toString()),
          });
          form.setValue("function_id", functionId.toString());
        } else {
          setData({
            ...data,
            functions: unFilteredFunctions,
          });
          form.setValue("function_id", "");
        }
      }

      if (selectedSow.department_ids) {
        if (selectedSow.department_ids.length > 1) {
          setData({
            ...data,
            departments: unFilteredDepartments.filter((dept: any) =>
              selectedSow.department_ids.includes(dept.id)
            ),
          });

          form.setValue("department_id", "");
        } else if (selectedSow.department_ids.length === 1) {
          const departmentId = selectedSow.department_ids[0];
          setData({
            ...data,
            departments: unFilteredDepartments.filter((dept: any) => dept.id.toString() === departmentId.toString()),
          });
          form.setValue("department_id", departmentId.toString());
        } else {
          setData({
            ...data,
            departments: unFilteredDepartments,
          });
          form.setValue("department_id", "");
        }
      }
    }
  }
  

  const handleEditorChange = (data: string) => {
    setEditorData(data);
    form.setValue('description', data);
  };
  
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-8 w-full"
      >
        {loading && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <Spinner />
          </div>
        )}
        <div className="w-full rounded-md bg-mauve6 shadow-md p-6">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-6">
            {/* Account Section */}
            <div className="flex flex-col">
              <FormField
                control={form.control}
                name="account_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-500 dark:text-darkTextColor">Account*</FormLabel>
                    <FormControl>
                      <Select
                        className="w-full"
                        options={formData.accounts.map((account) => ({
                          label: account.name,
                          value: account.id.toString(),
                        }))}
                        value={field.value}
                        onChange={(value) => handleAccountChange({ value, field, data: formData, setData: setFormData })}
                        disabled={mode === "edit"}
                        enabledSearch={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Conditional Rendering for Internal Owner */}
            {selectedAccountId && (
              <div className="flex flex-col">

                <FormField
                  control={form.control}
                  name="internal_owner_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Internal Owner</FormLabel>
                      <FormControl>
                        <Select
                          className="w-full"
                          options={formData.internalOwners?.map((internalOwner) => ({
                            label: internalOwner.name,
                            value: internalOwner.id.toString(),
                          })) || []}
                          value={field.value}
                          onChange={(value) => handleInternalOwnerChange({ value, field, data: formData, setData: setFormData })}
                          disabled={!edit}
                          enabledSearch={true}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </div>
            )}

            {/* Conditional Rendering for SOW */}
            {selectedAccountId && (
              <div className="flex flex-col">


                <FormField
                  control={form.control}
                  name="sow_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">SOW</FormLabel>
                      <FormControl>
                        <Select
                          className="w-full"
                          options={formData.sows?.map((sow) => ({
                            label: sow.name,
                            value: sow.id.toString(),
                          })) || []}
                          value={field.value}
                          onChange={(value) => handleSowChange({ value, field, data: formData, setData: setFormData })}
                          disabled={!edit}
                          enabledSearch={true}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
          {selectedAccountId && (<>
            <div className="flex w-full flex-col sm:flex-row items-center gap-2 pb-6">
              <label className="text-sm text-gray-500 w-full sm:w-40">
                <span className="text-sm text-gray-500 dark:text-darkTextColor" >Subject</span>
              </label>
              <div className="w-full">
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem className="mt-2">
                      <FormControl>
                        <Input {...field} value={field.value} placeholder="" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

            </div>
            <div className="flex flex-col sm:flex-row items-center gap-2 pb-6">
              <label className="text-sm text-gray-500 w-full sm:w-40">
                <span className="text-sm text-gray-500 dark:text-darkTextColor">Comment</span>
              </label>
              <div className="w-full">
                <div className="w-full">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem className="mt-2">
                        <FormControl>
                          <CKEditorWithFileUpload
                            value={editorData}
                            onChange={handleEditorChange}
                            isPublic={true}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="w-full flex justify-between items-center gap-2 pb-4">
              <div>
                <div className="flex">
                  <FormField
                    control={form.control}
                    name="files"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Attachments</FormLabel>
                        <FormControl>
                          <FileUpload
                            key={fileUploadKey}
                            files={field.value || []}
                            onChange={(files) => field.onChange(files)}
                            setAttachmentUploading={setAttachmentUploading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

              </div>
              <div>
                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Due Date</FormLabel>
                      <FormControl>
                        <DatePicker placeholder="Due Date" value={field.value ? new Date(field.value) : undefined} onChange={field.onChange} disabled={!edit} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <div className="w-full flex flex-wrap items-center gap-2 pb-4">
              <div className="w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-6">
                  <FormField
                    control={form.control}
                    name="priority_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Priority</FormLabel>
                        <FormControl>
                          <Select
                            options={formData.priorities.map((priority) => ({
                              label: priority.name,
                              value: priority.id.toString(),
                            }))}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select"
                            enabledSearch={true}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Requester</FormLabel>
                        <FormControl>
                          <Select
                            options={formData.requesters?.map((requester) => ({
                              label: requester.name,
                              value: requester.id.toString(),
                            })) || []}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select"
                            enabledSearch={true}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />


                  <div>
                    <FormField
                      control={form.control}
                      name="ticket_type_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Ticket Type</FormLabel>
                          <FormControl>
                            <Select
                              options={formData.ticket_types.map((ticketType) => ({
                                label: ticketType.name,
                                value: ticketType.id.toString(),
                              }))}
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                  
                                // Check if the selected option has children
                                const selectedOption = formData.ticket_types.find(
                                  (type) => type.id.toString() === value
                                );

                                if (selectedOption && selectedOption.children && selectedOption.children.length > 0) {
                                  form.setValue("sub_ticket_type_id", null); // Reset sub-ticket type
                                  setSubTicketTypes(selectedOption.children.filter(child => child.status));
                                } else {
                                  form.setValue("sub_ticket_type_id", undefined); // Remove sub-ticket type
                                  setSubTicketTypes([]);
                                }
                              }}
                              placeholder="Select"
                              enabledSearch={true}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {subTicketTypes.length > 0 && (
                        <FormField
                          control={form.control}
                          name="sub_ticket_type_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Sub Ticket Type</FormLabel>
                              <FormControl>
                                <Select
                                  options={subTicketTypes.map((child) => ({
                                      label: child.name,
                                      value: child.id.toString(),
                                    })) || []}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Sub Ticket Type"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                  </div>
                </div>
              </div>

            </div>

            <div className="flex justify-center mb-4">
              <div className="w-4/5 border-b-2 border-border"></div>
            </div>

            <div className="w-full flex flex-wrap items-center gap-2 pb-4">
              <div className="w-full">
                <div className="">
                  <InternalTicketTypeSection
                    form={form}
                    internalTicketTypes={formData?.internal_ticket_types || []}
                    edit={edit}
                    existingRows={ticket?.internal_ticket_types || []}
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-center mb-4">
              <div className="w-4/5 border-b-2 border-border"></div>
            </div>

            <div className="w-full flex flex-wrap items-center gap-2 pb-4">
              <div className="w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-6">
                  <div>
                  {formData.countries[0]?.children?.length > 0 ? (
                      <>
                        {/* Region ID Select */}
                        <FormField
                          control={form.control}
                          name="region_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Region</FormLabel>
                              <FormControl>
                                <Select
                                  options={formData.countries.map((region) => ({
                                    label: region.name,
                                    value: region.id.toString(),
                                  }))}
                                  value={field.value}
                                  onChange={(value) => {
                                    field.onChange(value);

                                    // Reset country_ids when the region changes
                                    const selectedRegion = formData.countries.find(
                                      (region) => region.id.toString() === value
                                    );
                                    if (selectedRegion?.children?.length) {
                                      form.setValue("country_ids", []); // Reset country_ids
                                      setSubCountries(selectedRegion.children.filter(child => child.status));
                                    } else {
                                      setSubCountries([]);
                                    }
                                  }}
                                  placeholder="Select Region"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Country IDs Select */}
                        {subCountries.length > 0 && (
                          <FormField
                            control={form.control}
                            name="country_ids"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Countries</FormLabel>
                                <FormControl>
                                  <Select
                                    multiple
                                    options={
                                      subCountries.map((country) => ({
                                          label: country.name,
                                          value: country.id.toString(),
                                        })) || []
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Select Countries"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </>
                    ) : (
                      /* Direct Country IDs Select */
                      <FormField
                        control={form.control}
                        name="country_ids"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Countries</FormLabel>
                            <FormControl>
                              <Select
                                multiple
                                options={formData.countries.map((country) => ({
                                  label: country.name,
                                  value: country.id.toString(),
                                }))}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select Countries"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  <div>
                    <FormField
                      control={form.control}
                      name="function_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Function</FormLabel>
                          <FormControl>
                            <Select
                              options={formData.functions.map((ticketFunction) => ({
                                label: ticketFunction.name,
                                value: ticketFunction.id.toString(),
                              }))}
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                  
                                // Check if the selected option has children
                                const selectedOption = formData.functions.find(
                                  (func) => func.id.toString() === value
                                );
                                if (selectedOption && selectedOption.children && selectedOption.children.length > 0) {
                                  form.setValue("sub_function_id", null); // Reset sub-function
                                  setSubFunctions(selectedOption.children.filter(child => child.status));
                                } else {
                                  form.setValue("sub_function_id", undefined); // Remove sub-function
                                  setSubFunctions([]);
                                }
                              }}
                              placeholder="Select"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {subFunctions.length > 0 && (
                        <FormField
                          control={form.control}
                          name="sub_function_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Sub Function</FormLabel>
                              <FormControl>
                                <Select
                                  options={subFunctions.map((child) => ({
                                      label: child.name,
                                      value: child.id.toString(),
                                    })) || []}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Sub Function"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                  </div>
                  
                  {formData.departments.length > 0 && (
                    <div>
                      <FormField
                        control={form.control}
                        name="department_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Department</FormLabel>
                            <FormControl>
                              <Select
                                options={formData.departments.map((department) => ({
                                  label: department.name,
                                  value: department.id.toString(),
                                }))}
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                    
                                  // Check if the selected option has children
                                  const selectedOption = formData.departments.find(
                                    (dept) => dept.id.toString() === value
                                  );
                                  if (selectedOption && selectedOption.children && selectedOption.children.length > 0) {
                                    form.setValue("sub_department_id", null); // Reset sub-department
                                    setSubDepartments(selectedOption.children.filter(child => child.status));
                                  } else {
                                    form.setValue("sub_department_id", undefined); // Remove sub-department
                                    setSubDepartments([]);
                                  }
                                }}
                                placeholder="Select"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {subDepartments.length > 0 && (
                          <FormField
                            control={form.control}
                            name="sub_department_id"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm text-gray-500 dark:text-darkTextColor">Sub Department</FormLabel>
                                <FormControl>
                                  <Select
                                    options={subDepartments.map((child) => ({
                                        label: child.name,
                                        value: child.id.toString(),
                                      })) || []}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Select Sub Department"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                    </div>

                  )}
                    
                </div>
              </div>

            </div>
          </>)}
        </div>
        {selectedAccountId && (<div className="flex justify-end gap-2 pb-32">
          {mode === "create" && (
            <Link href="/tickets">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          )}
          <Button type="submit" disabled={!edit || loading || attachmentUploading}>
            {mode === "create" ? "Create Ticket" : "Update Ticket"}
          </Button>
        </div>)}
      </form>
    </Form>
  );
};

export default TicketForm;
